import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '../OnboardingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Briefcase, Building2, ChevronRight, ChevronDown, Search } from 'lucide-react';
import { useAutoScroll } from '@/hooks/useAutoScroll';
import { useScrollToActivate } from '@/hooks/useScrollToActivate';

// Employment status IDs (stored in English in backend)
const employmentStatuses = [
  { id: 'employed', icon: Briefcase },
  { id: 'self-employed', icon: Building2 },
  { id: 'business-owner', icon: Building2 },
  { id: 'unemployed', icon: Briefcase },
  { id: 'student', icon: Briefcase },
  { id: 'retired', icon: Briefcase },
];

// Industry category IDs (stored in English in backend)
const industryCategories = [
  {
    id: 'agriculture',
    subcategories: ['crop', 'animal', 'forestry', 'fishing']
  },
  {
    id: 'mining',
    subcategories: ['coal', 'oil', 'metal', 'quarrying']
  },
  {
    id: 'manufacturing',
    subcategories: ['food', 'textiles', 'pharma', 'electronics', 'machinery', 'vehicles', 'furniture']
  },
  {
    id: 'utilities',
    subcategories: ['electric', 'gas', 'steam']
  },
  {
    id: 'water',
    subcategories: ['collection', 'sewerage', 'waste', 'recycling']
  },
  {
    id: 'construction',
    subcategories: ['building', 'civil', 'electrical', 'plumbing', 'painting']
  },
  {
    id: 'trade',
    subcategories: ['wholesale', 'retail', 'vehicles', 'ecommerce']
  },
  {
    id: 'transport',
    subcategories: ['rail', 'road', 'sea', 'air', 'warehouse', 'postal']
  },
  {
    id: 'hospitality',
    subcategories: ['hotels', 'holiday', 'restaurants', 'catering', 'bars']
  },
  {
    id: 'information',
    subcategories: ['publishing', 'film', 'broadcasting', 'telecom', 'software', 'it']
  },
  {
    id: 'financial',
    subcategories: ['banking', 'investment', 'insurance', 'pension', 'fund', 'advisory']
  },
  {
    id: 'realestate',
    subcategories: ['development', 'management', 'agents', 'valuation']
  },
  {
    id: 'professional',
    subcategories: ['legal', 'accounting', 'consulting', 'architecture', 'engineering', 'research', 'advertising']
  },
  {
    id: 'admin',
    subcategories: ['recruitment', 'security', 'cleaning', 'callcentre', 'travel']
  },
  {
    id: 'public',
    subcategories: ['government', 'defence', 'order', 'social']
  },
  {
    id: 'education',
    subcategories: ['primary', 'secondary', 'higher', 'vocational', 'tutoring']
  },
  {
    id: 'health',
    subcategories: ['hospitals', 'medical', 'dental', 'nursing', 'socialwork', 'childcare']
  },
  {
    id: 'arts',
    subcategories: ['performing', 'museums', 'sports', 'gambling', 'amusement']
  },
  {
    id: 'other',
    subcategories: ['membership', 'repair', 'personal', 'funeral']
  },
];

export function EmploymentStep() {
  const { t } = useTranslation();
  const { goToNextStep, updateData, data } = useOnboarding();
  const { scrollToRef } = useAutoScroll();
  const { canActivate, recheckScroll } = useScrollToActivate();
  
  // Initialize from context to preserve data on back navigation
  const [status, setStatus] = useState(() => data.employmentStatus || '');
  const [industry, setIndustry] = useState(() => data.industry || '');
  const [industrySearch, setIndustrySearch] = useState('');
  const [showIndustryList, setShowIndustryList] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [employerName, setEmployerName] = useState(() => data.employerDetails?.name || '');
  const [employerAddress, setEmployerAddress] = useState(() => data.employerDetails?.address || '');
  const [businessName, setBusinessName] = useState(() => data.businessDetails?.name || '');
  const [businessRegNumber, setBusinessRegNumber] = useState(() => data.businessDetails?.registrationNumber || '');
  const [jobTitle, setJobTitle] = useState(() => data.employerDetails?.jobTitle || '');
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Refs for auto-scrolling
  const employerDetailsRef = useRef<HTMLDivElement>(null);
  const businessDetailsRef = useRef<HTMLDivElement>(null);

  // Map status ID to translation key
  const getStatusLabel = (statusId: string) => {
    const keyMap: Record<string, string> = {
      'employed': 'employed',
      'self-employed': 'selfEmployed',
      'business-owner': 'businessOwner',
      'unemployed': 'unemployed',
      'student': 'student',
      'retired': 'retired',
    };
    return t(`employment.statuses.${keyMap[statusId] || statusId}`);
  };

  // Get translated category title
  const getCategoryTitle = (categoryId: string) => {
    return t(`employment.industries.${categoryId}.title`);
  };

  // Get translated subcategory label
  const getSubcategoryLabel = (categoryId: string, subcategoryId: string) => {
    return t(`employment.industries.${categoryId}.${subcategoryId}`);
  };

  // Get display label for selected industry (translated)
  const getIndustryDisplayLabel = () => {
    if (!industry) return '';
    // Industry is stored as "categoryId:subcategoryId" in backend
    const [catId, subId] = industry.split(':');
    if (catId && subId) {
      return `${getCategoryTitle(catId)} - ${getSubcategoryLabel(catId, subId)}`;
    }
    return industry;
  };

  const handleStatusSelect = (newStatus: string) => {
    setStatus(newStatus);
    // Auto-scroll to show the details form
    setTimeout(() => {
      if (newStatus === 'employed') {
        scrollToRef(employerDetailsRef);
      } else if (newStatus === 'business-owner' || newStatus === 'self-employed') {
        scrollToRef(businessDetailsRef);
      }
      recheckScroll();
    }, 150);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowIndustryList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter categories and subcategories based on search
  const filteredCategories = industryCategories
    .map(cat => ({
      ...cat,
      subcategories: cat.subcategories.filter(sub => {
        const subLabel = getSubcategoryLabel(cat.id, sub).toLowerCase();
        const catTitle = getCategoryTitle(cat.id).toLowerCase();
        const search = industrySearch.toLowerCase();
        return subLabel.includes(search) || catTitle.includes(search);
      })
    }))
    .filter(cat => {
      const catTitle = getCategoryTitle(cat.id).toLowerCase();
      return catTitle.includes(industrySearch.toLowerCase()) || cat.subcategories.length > 0;
    });

  // Auto-expand matching category when searching
  useEffect(() => {
    if (industrySearch.length > 0 && filteredCategories.length > 0) {
      const matchingCategory = filteredCategories.find(cat => 
        cat.subcategories.some(sub => 
          getSubcategoryLabel(cat.id, sub).toLowerCase().includes(industrySearch.toLowerCase())
        )
      );
      if (matchingCategory) {
        setExpandedCategory(matchingCategory.id);
      } else if (filteredCategories.length === 1) {
        setExpandedCategory(filteredCategories[0].id);
      }
    } else if (industrySearch.length === 0) {
      setExpandedCategory(null);
    }
  }, [industrySearch]);

  const needsEmployerDetails = status === 'employed';
  const needsBusinessDetails = status === 'business-owner' || status === 'self-employed';
  const needsIndustry = needsEmployerDetails || needsBusinessDetails;

  const isValid = () => {
    if (!status) return false;
    if (needsEmployerDetails && (!employerName || !jobTitle || !industry)) return false;
    if (needsBusinessDetails && (!businessName || !industry)) return false;
    return true;
  };

  const handleContinue = () => {
    updateData({
      employmentStatus: status, // Stored in English
      industry: industry, // Stored as "categoryId:subcategoryId" in English
      employerDetails: needsEmployerDetails ? {
        name: employerName,
        address: employerAddress,
        jobTitle: jobTitle,
      } : undefined,
      businessDetails: needsBusinessDetails ? {
        name: businessName,
        registrationNumber: businessRegNumber,
      } : undefined,
    });
    goToNextStep();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full overflow-y-auto">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
          <Briefcase className="w-8 h-8 text-accent" />
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-3">
          {t('employment.title')}
        </h1>
        
        <p className="text-muted-foreground mb-6">
          {t('employment.subtitle')}
        </p>

        <div className="space-y-3 mb-6">
          {employmentStatuses.map((emp) => {
            const Icon = emp.icon;
            return (
              <button
                key={emp.id}
                onClick={() => handleStatusSelect(emp.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                  status === emp.id
                    ? 'border-accent bg-accent/10'
                    : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                }`}
              >
                <Icon className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium text-foreground flex-1 text-left">{getStatusLabel(emp.id)}</span>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            );
          })}
        </div>

        {needsIndustry && (
          <div className="space-y-4 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('employment.industryLabel')} <span className="text-destructive">*</span>
              </label>
              <div className="relative" ref={dropdownRef}>
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
                <Input
                  value={industrySearch || getIndustryDisplayLabel()}
                  onChange={(e) => {
                    setIndustrySearch(e.target.value);
                    setShowIndustryList(true);
                    if (!e.target.value) setIndustry('');
                  }}
                  onFocus={() => setShowIndustryList(true)}
                  placeholder={t('employment.industryPlaceholder')}
                  className="pl-12 h-14 bg-muted/30 border-border rounded-xl"
                />
                {showIndustryList && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-2xl z-50 max-h-64 overflow-y-auto">
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map((cat) => (
                        <div key={cat.id}>
                          <button
                            onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                            className="w-full px-4 py-3 text-left hover:bg-muted/50 text-foreground font-medium flex items-center justify-between border-b border-border/50"
                          >
                            <span>{getCategoryTitle(cat.id)}</span>
                            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expandedCategory === cat.id ? 'rotate-180' : ''}`} />
                          </button>
                          {expandedCategory === cat.id && (
                            <div className="bg-muted/20">
                              {cat.subcategories.map((sub) => (
                                <button
                                  key={sub}
                                  onClick={() => {
                                    // Store as "categoryId:subcategoryId" for backend (English)
                                    setIndustry(`${cat.id}:${sub}`);
                                    setIndustrySearch(getSubcategoryLabel(cat.id, sub));
                                    setShowIndustryList(false);
                                    setExpandedCategory(null);
                                  }}
                                  className="w-full px-8 py-2.5 text-left hover:bg-accent/20 text-foreground/80 text-sm"
                                >
                                  {getSubcategoryLabel(cat.id, sub)}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-muted-foreground">{t('employment.noMatchingIndustries')}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {needsEmployerDetails && (
          <div ref={employerDetailsRef} className="space-y-4 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('employment.employerName')}
              </label>
              <Input
                value={employerName}
                onChange={(e) => setEmployerName(e.target.value)}
                placeholder={t('employment.employerNamePlaceholder')}
                className="h-14 bg-muted/30 border-border rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('employment.jobTitle')}
              </label>
              <Input
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder={t('employment.jobTitlePlaceholder')}
                className="h-14 bg-muted/30 border-border rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('employment.employerAddress')}
              </label>
              <Input
                value={employerAddress}
                onChange={(e) => setEmployerAddress(e.target.value)}
                placeholder={t('employment.employerAddressPlaceholder')}
                className="h-14 bg-muted/30 border-border rounded-xl"
              />
            </div>
          </div>
        )}

        {needsBusinessDetails && (
          <div ref={businessDetailsRef} className="space-y-4 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('employment.businessName')}
              </label>
              <Input
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder={t('employment.businessNamePlaceholder')}
                className="h-14 bg-muted/30 border-border rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('employment.registrationNumber')}
              </label>
              <Input
                value={businessRegNumber}
                onChange={(e) => setBusinessRegNumber(e.target.value)}
                placeholder={t('employment.registrationNumberPlaceholder')}
                className="h-14 bg-muted/30 border-border rounded-xl"
              />
            </div>
          </div>
        )}

      </div>
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleContinue}
            disabled={!isValid() || !canActivate}
            className="w-full h-14 text-lg font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            {t('common.continue')}
          </Button>
        </div>
      </div>
    </div>
  );
}
